import { useState, useEffect, useCallback } from "react";
import {
  substrateWalletService,
  type WalletInfo,
} from "../services/walletService";
import type { InjectedAccountWithMeta } from "@polkadot/extension-inject/types";

export interface UseWalletReturn {
  wallet: WalletInfo | null;
  isConnecting: boolean;
  availableWallets: string[];
  connectWallet: (
    walletSource: string,
    selectedAccount?: InjectedAccountWithMeta,
    setSelectedAccount?: React.Dispatch<InjectedAccountWithMeta | undefined>
  ) => Promise<void>;
  disconnectWallet: (setSelectedAccount: React.Dispatch<InjectedAccountWithMeta | undefined>) => Promise<void>;
  getBalance: (address: string) => Promise<string>;
  switchNetwork: (chainId: string) => Promise<void>;
  getAccounts: (walletSource?: string) => Promise<any[]>;
}

export const useWallet = (): UseWalletReturn => {
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [availableWallets, setAvailableWallets] = useState<string[]>([]);

  useEffect(() => {
    const initializeWallets = async () => {
      try {
        const wallets = await substrateWalletService.getAvailableWallets();
        setAvailableWallets(wallets);
      } catch (error) {
        console.error("Failed to get available wallets:", error);
        setAvailableWallets([]);
      }
    };

    initializeWallets();
  }, []);

  useEffect(() => {
    const currentWallet = substrateWalletService.getCurrentWallet();
    if (currentWallet) {
      setWallet(currentWallet);
    }
  }, []);

  useEffect(() => {
    const syncWalletState = () => {
      const currentWallet = substrateWalletService.getCurrentWallet();
      if (
        currentWallet &&
        (!wallet || wallet.address !== currentWallet.address)
      ) {
        setWallet(currentWallet);
      }
    };

    syncWalletState();

    const interval = setInterval(syncWalletState, 1000);

    return () => clearInterval(interval);
  }, [wallet]);

  const connectWallet = useCallback(
    async (
      walletSource: string,
      selectedAccount?: InjectedAccountWithMeta,
      setSelectedAccount?: React.Dispatch<InjectedAccountWithMeta | undefined>
    ) => {
      setIsConnecting(true);
      try {
        const connectedWallet = await substrateWalletService.connectWallet(
          walletSource,
          selectedAccount,
          setSelectedAccount
        );
        setWallet(connectedWallet);
      } catch (error) {
        console.error("Failed to connect wallet:", error);
        throw error;
      } finally {
        setIsConnecting(false);
      }
    },
    [availableWallets]
  );

  const disconnectWallet = useCallback(async (setSelectedAccount: React.Dispatch<InjectedAccountWithMeta | undefined>) => {
    try {
      await substrateWalletService.disconnectWallet(setSelectedAccount);
      setWallet(null);
    } catch (error) {
      console.error("Failed to disconnect wallet:", error);
      throw error;
    }
  }, []);

  const getBalance = useCallback(async (address: string): Promise<string> => {
    return await substrateWalletService.getBalance(address);
  }, []);

  const switchNetwork = useCallback(async (chainId: string): Promise<void> => {
    try {
      await substrateWalletService.switchNetwork(chainId);
      const currentWallet = substrateWalletService.getCurrentWallet();
      if (currentWallet) {
        setWallet(currentWallet);
      }
    } catch (error) {
      console.error("Failed to switch network:", error);
      throw error;
    }
  }, []);

  const getAccounts = useCallback(
    async (walletSource?: string): Promise<any[]> => {
      return await substrateWalletService.getAccounts(walletSource);
    },
    []
  );

  return {
    wallet,
    isConnecting,
    availableWallets,
    connectWallet,
    disconnectWallet,
    getBalance,
    switchNetwork,
    getAccounts,
  };
};
